import React, {FormEvent, useEffect, useRef, useState} from "react";
import dayjs from 'dayjs'
import SelfcarePageContent from "../../../components/selfcare/SelfcarePageContent";
import {competitor, selfcareRegisterSbbu} from "../../../util/request";
import {useCustomer} from "../../../layouts/SelfcareLayout";
import {Competitor, Customer} from "../../../util/types";
import {fullname} from "../../../util/formatter";
import {Link, useNavigate} from "react-router-dom";
import {Details} from "../../../components/selfcare/Details";
import {useDetailsState} from "../../../components/selfcare/useDetailsState.tsx";
import Routing from "../../../Routing.ts";
import now from "../../../util/now.ts";
import setTitleAndMetaDescription from "../../../components/setTitleAndMetaDescription.tsx";

export default function SbbuPage() {
    setTitleAndMetaDescription("Skift af udbyder - Mit Hiper", "Her kan du se og betale dine udestående regninger.");

    const {customer} = useCustomer();
    const navigate = useNavigate();
    const detailsState = useDetailsState({open: false});

    const onSuccess = () => {
        navigate(Routing.Selfcare.TerminateExistingReceipt)
    }

    return <SelfcarePageContent className={'selfcare-page-sbbu'}>
        <div className="selfcare-page-summary">
            <h1 className="heading heading--big">Skal vi opsige din nuværende udbyder for dig?</h1>
            <p>Udfyld nedenstående felter &ndash; så tager vi os af al praktikken.</p>
        </div>

        <SbbuForm for={customer} onSuccess={onSuccess} fieldsets={(disabled) => <>
            <SbbuFormFieldset
                className={'personal-information'}
                disabled={disabled}
            >
                <Details {...detailsState}>
                    <summary>
                        <span className={'title'}>Dine præudfyldte oplysninger</span>
                        <i />
                    </summary>
                    <div>
                            <NameAndCvr customer={customer}/>
                            <Address customer={customer}/>
                            <InstallationAddress/>
                    </div>
                </Details>
            </SbbuFormFieldset>

            <SbbuFormFieldset
                number={1}
                title={'Din nuværende udbyder'}
                className={'sbbu-competitor'}
                disabled={disabled}
            >
                <CompetitorSelect/>
            </SbbuFormFieldset>

            <hr/>

            <SbbuFormFieldset
                number={2}
                title={'Dato for flytning'}
                className={'sbbu-date'}
                disabled={disabled}
            >
                <SbbuDate/>
            </SbbuFormFieldset>

            <hr/>

            <SbbuFormFieldset
                number={3}
                title={'Acceptér'}
                className={'sbbu-consent'}
                disabled={disabled}
            >
                <SbbuMarketingConsent/>
                <SbbuConsent/>
                <SbbuSubmit/>
            </SbbuFormFieldset>
        </>}/>
    </SelfcarePageContent>
}

function SbbuForm(props: { for: Customer, onSuccess: () => void, fieldsets: (disabled: boolean) => React.ReactNode }) {
    const [state, setState] = useState<'initial' | 'submitting' | 'success' | 'error'>('initial');

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        setState('submitting')
        const form = event.target;
        const formData = new FormData(form as HTMLFormElement);
        const formJson = Object.fromEntries(formData.entries());

        selfcareRegisterSbbu(props.for.customer_id, formJson)
            .then(() => setState('success'))
            .catch(() => {
                setState('error')
            })
    }

    if (state === 'success') {
        props.onSuccess()
        return <></>
    }

    if (state === 'error') {
        return <SbbuError/>
    }

    return <form
        className="sbbu-form sbbu-form-1-2-3"
        id="sbbu-form"
        method="post"
        onSubmit={onSubmit}
    >
        {props.fieldsets(state === 'submitting')}
    </form>
}

interface SbbuFormFieldsetProps {
    number?: number;
    title?: string;
    className?: string;
    disabled?: boolean;
    children: React.ReactNode;
}

function SbbuFormFieldset({number, title, className, disabled = false, children}: SbbuFormFieldsetProps) {
    return <fieldset className={className} disabled={disabled}>
        {title && (
            <legend>
                <h3>
                    {number && (
                        <span className={'number'}>{number}.{' '}</span>
                    )}
                    <span className={'text'}>{title}</span>
                </h3>
            </legend>
        )}
        {children}
    </fieldset>
}

function SbbuError() {
    const navigate = useNavigate();
    return <div className="flex flex-column flex-center">
        <p>Noget gik galt. Prøv igen senere.</p>
        <Link to={""} onClick={() => navigate(0)}>Prøv igen</Link>
    </div>;
}

function SbbuConsent() {
    return <p className="with-checkbox">
        <input
            type="checkbox"
            id="sbbu-consent"
            name="sbbu_consent"
            defaultValue={1}
            required
        />
        <label
            htmlFor="sbbu-consent"
            className="checkbox-label checkbox-label--montserrat"
        >
            <span className={'title'}>Fuldmagt &ndash; skift af internetudbyder</span>
            <strong>Ja, jeg giver tilladelse til</strong>, at Hiper A/S på mine vegne opsiger mit internetabonnement hos min nuværende udbyder og flytter internetforbindelsen
            til et nyt abonnement hos Hiper A/S.
        </label>
    </p>
}

function NameAndCvr({customer}: { customer: Customer }) {
    return <>
        <div className="input-container">
            <label htmlFor="sbbu-name">
                Navn eller virksomhed
            </label>
            <input
                type="text"
                id="sbbu-name"
                name="sbbu_name"
                placeholder="Navn"
                defaultValue={fullname(customer.customer_contact)}
                required
            />
        </div>
        <div className="input-container">
            <label htmlFor="sbbu-cvr">
                CVR-nummer (kun erhvervskunder)
            </label>
            <input
                type="text"
                id="sbbu-cvr"
                name="sbbu_cvr"
                defaultValue={customer.customer_contact.cvr}
                placeholder="CVR-nummer"
            />
        </div>
    </>
}

function Address({customer}: { customer: Customer }) {
    return <div className="input-container">
        <label>
            Adresse
        </label>
        <div className="address">
            <input
                type="text"
                name="sbbu_street_name"
                placeholder="Vej"
                title="Dit vejnavn. F.eks. Teglholmsgade"
                defaultValue={customer.customer_contact.street_name}
                required
            />
            <input
                type="text"
                name="sbbu_street_number"
                placeholder="Hus nr"
                title="Dit husnummer"
                defaultValue={`${customer.customer_contact.street_number}${customer.customer_contact.street_letter || ''}`}
                required
            />
            <input
                type="text"
                name="sbbu_street_floor"
                placeholder="Etage"
                defaultValue={customer.customer_contact.street_floor}
                title="Din etage"
            />
            <input
                type="text"
                name="sbbu_street_side"
                placeholder="Side"
                defaultValue={customer.customer_contact.street_side}
                title="Side"
            />
            <input
                id="zip"
                type="text"
                name="sbbu_street_zip"
                placeholder="Postnummer"
                title="Dit postnummer"
                defaultValue={customer.customer_contact.street_zip}
                required
            />
            <input
                id="city"
                type="text"
                name="sbbu_street_city"
                placeholder="By"
                title="Din by"
                defaultValue={customer.customer_contact.city}
                required
            />
        </div>
    </div>
}

function InstallationAddress() {
    const detailsState = useDetailsState({open: false});

    return <div className="input-container">
        <label>
            Installationsadresse (hvis forskellig fra kundeadresse)
        </label>
        <Details className={'installation-address'} {...detailsState}>
            <summary>
                <span className={'title'}>Installationsadresse</span>
                <i />
            </summary>
            <div className="address">
                <input
                    type="text"
                    name="sbbu_installation_street_name"
                    placeholder="Vej"
                    title="Dit vejnavn. F.eks. Teglholmsgade"
                />
                <input
                    type="text"
                    name="sbbu_installation_street_number"
                    placeholder="Hus nr"
                    title="Dit husnummer"
                />
                <input
                    type="text"
                    name="sbbu_installation_street_floor"
                    placeholder="Etage"
                    title="Din etage"
                />
                <input
                    type="text"
                    name="sbbu_installation_street_side"
                    placeholder="Side"
                    title="Din side"
                />
                <input
                    type="text"
                    name="sbbu_installation_street_zip"
                    placeholder="Postnummer"
                    title="Dit postnummer"
                />
                <input
                    type="text"
                    name="sbbu_installation_street_city"
                    placeholder="By"
                    title="Din by"
                />
            </div>
        </Details>
    </div>
}

function CompetitorSelect() {
    const [competitors, setCompetitors] = useState<Competitor[]>([])

    useEffect(() => {
        competitor().then(r => {
            setCompetitors(r.response.data.filter(c => c.competitor_show_in_sbbu))
        })
    }, [])

    const [chosenCompetitor, setChosenCompetitor] = useState<Competitor | undefined>(undefined)
    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const competitorId = parseInt(e.target.value)
        const competitor = competitors.find(c => c.competitor_id === competitorId)
        setChosenCompetitor(competitor)
    }

    const hint = chosenCompetitor === undefined || chosenCompetitor.competitor_help_text.length === 0
        ? 'Kundenummeret står normalt på regningen fra din nuværende udbyder.'
        : chosenCompetitor.competitor_help_text

    return <>
        <div className="input-container">
            <label htmlFor="sbbu-provider">
                Vælg din nuværende udbyder
            </label>
            <div className="select-container">
                <select
                    id="sbbu-provider"
                    name="sbbu_provider"
                    onChange={onChange}
                    defaultValue={''}
                    required
                >
                    <option
                        value=""
                        disabled
                    >
                        Vælg&hellip;
                    </option>
                    {competitors
                        .sort((a, b) => a.competitor_display_name.localeCompare(b.competitor_display_name))
                        .map(competitor => (
                            <option
                                key={competitor.competitor_id}
                                value={competitor.competitor_id}
                            >
                                {competitor.competitor_display_name}
                            </option>
                        ))
                    }
                </select>
            </div>
        </div>

        <div
            className={`input-container ${(chosenCompetitor !== undefined && chosenCompetitor.competitor_custom_provider) ? '' : 'sbbu-custom-provider-hidden'}`}
            id="sbbu-custom-provider"
        >
            <label htmlFor="sbbu-custom-provider-input">
                Kan du ikke finde din udbyder på listen?
            </label>
            <input
                type="text"
                id="sbbu-custom-provider-input"
                name="sbbu_custom_provider_input"
                placeholder="Anden udbyder"
            />
        </div>

        <div className="input-container">
            <label htmlFor="sbbu-reference">
                Kundenummer hos <strong>nuværende udbyder</strong>
            </label>
            <input
                type="text"
                id="sbbu-reference"
                name="sbbu_reference"
                placeholder="Kundenummer"
                required
            />
            <p className={'small'}>
                <strong>Tip:</strong> <span id="sbbu-hint">{hint}</span>
            </p>
        </div>
    </>
}

function SbbuDate() {
    const myRef = useRef(null);
    const minDate = now.add(14, 'day').toDate();
    const maxDate = now.add(14, 'day').add(6, 'month').toDate()

    useEffect(() => {
        if (myRef.current !== null) {
            window.flatpickr(myRef.current, {
                dateFormat: 'Y-m-d',
                defaultDate: minDate,
                minDate: minDate,
                maxDate: maxDate,
                shorthandCurrentMonth: false,
                inline: true,
                prevArrow: '&#9664;',
                nextArrow: '&#9654;',
                altFormat: null,
                altInput: null,
                disable: null,
            });
        }
    }, [minDate, maxDate])

    return <>

        <p className="no-space">
            Jeg ønsker, at mit internet bliver flyttet på følgende dato (eller hurtigst muligt herefter):
        </p>

        <p className="small">
            Tidligst om 14 dage og maks. 6 måneder fra d.d.
        </p>

        <p className={'flatpickr-wrapper inline'}>
            <input
                id="sbbu-date"
                ref={myRef}
                type="text"
                name="sbbu_date"
                defaultValue={dayjs(minDate).format('YYYY-MM-DD')}
            />
        </p>
        <p className="smaller">
            <strong>Bemærk:</strong> Har du ønsket en dato, der ligger før udløbet af din bindings- eller opsigelsesperiode hos din nuværende udbyder, har udbyderen ret til at
            opkræve betaling for den resterende del af din bindings- eller opsigelsesperiode.
            Hvis du vil undgå at betale for to abonnementer på samme tid, skal du vælge den dato, hvor din bindings- og opsigelsesperiode ophører hos din nuværende udbyder.
            Du kan finde oplysning om bindings- og opsigelsesperioden i abonnementsvilkårene hos din nuværende udbyder.
            Der kan gå op til 2 hverdage, fra vi modtager din fuldmagt, til vi kan give besked om opsigelsen til din nuværende udbyder.
            Derfor bliver din opsigelsesdato regnet fra den dato, hvor din nuværende udbyder har fået fuldmagten.
        </p>
        <p>
            <strong>
                Du får hurtigst muligt en mail med den endelige dato, hvor dit internet hos os er klar.
            </strong>
        </p>
    </>
}

function SbbuMarketingConsent() {
    return <p className="with-checkbox">
        <input
            type="checkbox"
            id="sbbu-marketing-opt-in"
            name="sbbu_marketing_opt_in"
        />
        <label htmlFor="sbbu-marketing-opt-in" className="checkbox-label checkbox-label--montserrat">
            <span className={'title'}>Tilbagetræk markedsføringssamtykke</span>
            Min nuværende internetudbyder <strong>må ikke kontakte mig med markedsføring</strong> om internet.
        </label>
    </p>
}

function SbbuSubmit() {
    return <>
        <p className="submit-button-flex">
            <label id="submit-form-button" className="button teal">
                <input className="continue-button-submit" type="submit"/>
                <span className="text">Indsend</span>
            </label>
        </p>
        <p className="small">
            Denne fuldmagt bortfalder automatisk efter 6 måneder.
        </p>
    </>
}
